/**
 * Admin Notification Helper for Shopify Theme
 *
 * This script provides a simple function to send notifications to admin WhatsApp numbers
 * from your Shopify store frontend.
 *
 * Usage:
 *   notifyAdmin("Your message here");
 *
 * Example:
 *   notifyAdmin("❌ Login Error\nUser: +962XXXXXX\nInvalid OTP entered");
 */

(function (window) {
  "use strict";

  // Configuration
  const CONFIG = {
    // Your Next.js API endpoint
    apiEndpoint: "https://api.jodi-so.com/api/admin-notify",

    // HMAC Secret (same as ADMIN_NOTIFY_SECRET in your .env)
    // Note: This is exposed in client-side code but combined with rate limiting
    // and timestamp validation for security
    hmacSecret:
      "a5f9d2f8a4b2a1f8e7d6c5b4a3f2e1d0c9b8a7f6e5d4c3b2a1f0e9d8c7b6a5f4",

    // Debug mode (set to false in production)
    debug: false,
  };

  /**
   * Convert string to UTF-8 byte array
   */
  function stringToBytes(str) {
    const bytes = new Uint8Array(str.length);
    for (let i = 0; i < str.length; i++) {
      bytes[i] = str.charCodeAt(i);
    }
    return bytes;
  }

  /**
   * Convert byte array to hex string
   */
  function bytesToHex(bytes) {
    return Array.from(bytes)
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
  }

  /**
   * HMAC-SHA256 implementation using Web Crypto API
   */
  async function hmacSHA256(message, secret) {
    const encoder = new TextEncoder();
    const keyData = encoder.encode(secret);
    const messageData = encoder.encode(message);

    // Import the secret key
    const key = await crypto.subtle.importKey(
      "raw",
      keyData,
      { name: "HMAC", hash: "SHA-256" },
      false,
      ["sign"]
    );

    // Sign the message
    const signature = await crypto.subtle.sign("HMAC", key, messageData);

    // Convert to hex string
    return bytesToHex(new Uint8Array(signature));
  }

  /**
   * Generate HMAC signature for message
   */
  async function generateSignature(message, timestamp) {
    const data = message + timestamp;
    return await hmacSHA256(data, CONFIG.hmacSecret);
  }

  /**
   * Send notification to admin
   *
   * @param {string} message - The message to send to admins
   * @returns {Promise<object>} Response from API
   */
  async function notifyAdmin(message) {
    // Validate message
    if (!message || typeof message !== "string") {
      console.error("[Admin Notify] Invalid message:", message);
      return { success: false, error: "Invalid message" };
    }

    try {
      // Get current timestamp
      const timestamp = Date.now().toString();

      // Generate HMAC signature
      const signature = await generateSignature(message, timestamp);

      if (CONFIG.debug) {
        console.log("[Admin Notify] Sending notification:", {
          messageLength: message.length,
          timestamp,
          signature: signature.substring(0, 16) + "...",
        });
      }

      // Send request to API
      const response = await fetch(CONFIG.apiEndpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          message,
          timestamp,
          signature,
        }),
      });

      const data = await response.json();

      if (CONFIG.debug) {
        console.log("[Admin Notify] Response:", data);
      }

      if (!response.ok) {
        console.error("[Admin Notify] Failed:", data.error);
        return {
          success: false,
          error: data.error,
          statusCode: response.status,
        };
      }

      return { success: true, data };
    } catch (error) {
      console.error("[Admin Notify] Error:", error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Convenience function to notify about errors with additional context
   *
   * @param {string} errorMessage - The error message
   * @param {object} context - Additional context (optional)
   */
  async function notifyError(errorMessage, context = {}) {
    let message = `❌ Error Alert\n\n${errorMessage}`;

    // Add context if provided
    if (context.page) {
      message += `\n\nPage: ${context.page}`;
    }
    if (context.user) {
      message += `\nUser: ${context.user}`;
    }
    if (context.action) {
      message += `\nAction: ${context.action}`;
    }
    if (context.details) {
      message += `\nDetails: ${context.details}`;
    }

    // Add timestamp
    message += `\n\nTime: ${new Date()
      .toISOString()
      .replace("T", " ")
      .substring(0, 19)}`;

    // Add browser info
    message += `\nBrowser: ${navigator.userAgent.split(/[()]/)[1] || "Unknown"
      }`;

    return await notifyAdmin(message);
  }

  /**
   * Convenience function to notify about warnings
   *
   * @param {string} warningMessage - The warning message
   */
  async function notifyWarning(warningMessage) {
    const message = `⚠️ Warning\n\n${warningMessage}\n\nTime: ${new Date()
      .toISOString()
      .replace("T", " ")
      .substring(0, 19)}`;
    return await notifyAdmin(message);
  }

  /**
   * Convenience function to notify about info/success events
   *
   * @param {string} infoMessage - The info message
   */
  async function notifyInfo(infoMessage) {
    const message = `ℹ️ Info\n\n${infoMessage}\n\nTime: ${new Date()
      .toISOString()
      .replace("T", " ")
      .substring(0, 19)}`;
    return await notifyAdmin(message);
  }

  // Expose functions globally
  window.notifyAdmin = notifyAdmin;
  window.notifyError = notifyError;
  window.notifyWarning = notifyWarning;
  window.notifyInfo = notifyInfo;

  // Also expose as an object for organized usage
  window.AdminNotify = {
    send: notifyAdmin,
    error: notifyError,
    warning: notifyWarning,
    info: notifyInfo,
    config: CONFIG, // Allow runtime configuration changes
  };

  if (CONFIG.debug) {
    console.log("[Admin Notify] Helper loaded successfully");
  }
})(window);
